import moment from 'moment-timezone';
import _ from 'lodash';
import {
  getGlobalFilter,
  getProjectCurrency,
  getProjectZoneId,
  getRevenueAmountFieldPath,
  getRevenueGroupColumn,
  getRevenuePaymentFilter,
  getRevenueQueryType,
  getRevenueSplitColumn,
} from '../reducers/analyticsApp';
import { toFilter } from './filterConverter';
import { revenueQueryTypes } from '../constants/revenue';
import convertUniqueUsersResult from './revenueConverter';
import { getTypeField } from '../utils/event/createQueries';
import { getAggregations, timeUnits, toEventResult, toRange } from './eventConverter';
import getCurrencySymbol from '../utils/revenue';
import { FULL_DATE_FORMAT } from '../constants/date';
import { AGGREGATIONS_CSV, COLUMNS_CSV } from '../utils/csv';
import { toCsvEvent } from './csv/csvConverter';

const getRevenueAggregationsCsv = (field, excludeTypes) =>
  [
    {
      type: AGGREGATIONS_CSV.COUNT,
      field: null,
    },
    {
      type: AGGREGATIONS_CSV.MIN,
      field,
    },
    {
      type: AGGREGATIONS_CSV.MAX,
      field,
    },
    {
      type: AGGREGATIONS_CSV.TOTAL,
      field,
    },
  ]
    // исключаем те типы агрегаций, которые уже присутствуют в запросе составленном для revenue.
    .filter(({ type }) => !excludeTypes.some((excludeType) => excludeType.includes(type)));

const createQuery = (state, eventName, queryType) => {
  const groupBy = getRevenueGroupColumn(state);
  const split = getRevenueSplitColumn(state);

  const groups = split ? [groupBy, split] : [groupBy];
  const amountField = getRevenueAmountFieldPath(state);

  const paymentFilter = toFilter(getRevenuePaymentFilter(state));

  const query = {
    eventName,
    aggregations: [
      {
        type: queryType === revenueQueryTypes.ARPU ? 'TOTAL' : 'USERS',
        field: queryType === revenueQueryTypes.ARPU ? amountField : null,
      },
    ],
    groupBy: groups,
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };

  if (eventName === 'Payment') {
    query.filter = paymentFilter;
  }

  return query;
};

const getRevenueAggregations = (queryType, amountField, split) => {
  switch (queryType) {
    case revenueQueryTypes.REVENUE:
      return [
        {
          type: 'TOTAL',
          field: amountField,
        },
      ];

    case revenueQueryTypes.TRANSACTIONS:
      return [
        {
          type: 'COUNT',
          field: null,
        },
      ];

    case revenueQueryTypes.ARPPU:
      return [
        {
          type: split ? 'AVERAGE_TOTAL_SPLIT' : 'AVERAGE_TOTAL',
          field: amountField,
        },
      ];

    default:
      throw new Error('Unrecognized query type');
  }
};

const createSummaryPaymentQuery = (state) => ({
  eventName: 'Payment',
  aggregations: [
    {
      type: 'TOTAL',
      field: getRevenueAmountFieldPath(state),
    },
    {
      type: 'USERS',
      field: null,
    },
  ],
  filter: toFilter(getRevenuePaymentFilter(state)),
  globalFilter: toFilter(getGlobalFilter(state)),
  timezone: getProjectZoneId(state),
});

const createUniqueUsersQuery = (state) => ({
  eventName: 'DAU',
  aggregations: [
    {
      type: 'USERS',
      field: null,
    },
  ],
  globalFilter: toFilter(getGlobalFilter(state)),
  timezone: getProjectZoneId(state),
});

const createEventRevenueQuery = (state) => {
  const groupBy = getRevenueGroupColumn(state);
  const split = getRevenueSplitColumn(state);
  const queryType = getRevenueQueryType(state);
  const groups = split ? [groupBy, split] : [groupBy];
  const amountField = getRevenueAmountFieldPath(state);

  return {
    eventName: 'Payment',
    aggregations: getRevenueAggregations(queryType, amountField, split),
    groupBy: groups,
    filter: toFilter(getRevenuePaymentFilter(state)),
    globalFilter: toFilter(getGlobalFilter(state)),
    timezone: getProjectZoneId(state),
  };
};

const mapSummaryPaymentResult = (query, result) => ({
  revenue: result.rows[0][`total.${query.aggregations[0].field}`],
  payers: result.rows[0].users,
});

const mapEventRevenueResult = (response, query, revenueInfo) => {
  const { getType, projectCurrencySymbol } = revenueInfo;
  const events = [toEventResult(response, query, '', getType)];
  const options = toRange(events[0].info);
  const selectedAmountField = events[0]?.info?.aggregation[0];
  const currencySymbol = getCurrencySymbol(projectCurrencySymbol, selectedAmountField);

  return {
    events,
    options,
    currencySymbol,
  };
};

const division = (a, b, queryType) =>
  queryType === revenueQueryTypes.ARPU ? _.round(a / b, 2) : _.round((a / b) * 100, 2);

const mapComposeRevenueResult = (
  composeRevenueInfo,
  payersPaymentQuery,
  payersUsersQuery,
  paymentResponse,
  userResponse
) => {
  const { queryType, getType } = composeRevenueInfo;
  const [groupBy, splitBy] = payersPaymentQuery.groupBy;

  const paymentAggregation = getAggregations(payersPaymentQuery.aggregations)[0];
  const userAggregation = getAggregations(payersUsersQuery.aggregations)[0];

  const rows = paymentResponse.rows
    .map((paymentRow) => {
      const userRow = userResponse.rows.find(
        (userRowItem) =>
          userRowItem[groupBy] === paymentRow[groupBy] &&
          userRowItem[splitBy] === paymentRow[splitBy]
      );

      if (userRow === undefined) {
        return userRow;
      }

      return splitBy === undefined
        ? {
            [groupBy]: userRow[groupBy],
            [queryType]: division(
              paymentRow[paymentAggregation],
              userRow[userAggregation],
              queryType
            ),
          }
        : {
            [groupBy]: userRow[groupBy],
            [queryType]: division(
              paymentRow[paymentAggregation],
              userRow[userAggregation],
              queryType
            ),
            [splitBy]: userRow[splitBy],
          };
    })
    .filter((row) => row !== undefined);

  return {
    events: [
      {
        type: 'eventsResult',
        info: {
          typeGroupBy: getType(groupBy).type,
          timezone: payersUsersQuery.timezone,
          eventName: queryType,
          query: payersUsersQuery,
          uniqueChar: '',
          aggregation: [queryType],
          groupBy,
          splitBy,
          timeUnit: timeUnits[groupBy],
        },
        result: {
          columns: [groupBy, splitBy, queryType].filter((c) => c !== undefined),
          rows: rows.filter((row) => Object.keys(row).length > 0),
        },
      },
    ],
    options: {
      from: moment
        .tz(payersUsersQuery.globalFilter.from, FULL_DATE_FORMAT, payersUsersQuery.timezone)
        .startOf(timeUnits[groupBy])
        .valueOf(),
      to: moment
        .tz(payersUsersQuery.globalFilter.to, FULL_DATE_FORMAT, payersUsersQuery.timezone)
        .startOf(timeUnits[groupBy])
        .valueOf(),
    },
  };
};

export const buildRevenueQueries = (state) => [
  createSummaryPaymentQuery(state),
  createUniqueUsersQuery(state),
  createEventRevenueQuery(state),
];

export const buildComposeRevenueQueries = (state) => [
  createSummaryPaymentQuery(state),
  createUniqueUsersQuery(state),
  createQuery(state, 'Payment', getRevenueQueryType(state)),
  createQuery(state, 'DAU'),
];

export const getRevenueInfo = (state) => {
  const queryType = getRevenueQueryType(state);
  const getType = (field) => getTypeField(field, state);
  const projectCurrencySymbol = getProjectCurrency(state);

  return { queryType, getType, projectCurrencySymbol };
};

export const fromRevenueResponse = (response, queries, revenueInfo) => {
  const { queryType } = revenueInfo;
  const eventRevenueQuery = queries[2];
  const [summaryPayment, uniqueUsersResult, eventRevenue] = response;
  const { revenue, payers } = mapSummaryPaymentResult(queries[0], summaryPayment);
  const { uniqueUsers } = convertUniqueUsersResult(uniqueUsersResult);
  const { events, options, currencySymbol } = mapEventRevenueResult(
    eventRevenue,
    eventRevenueQuery,
    revenueInfo
  );

  return { revenue, payers, uniqueUsers, events, options, currencySymbol, queryType };
};

export const getComposeRevenueInfo = (state) => {
  const queryType = getRevenueQueryType(state);
  const getType = (field) => getTypeField(field, state);

  return { queryType, getType };
};

export const fromComposeRevenueResponse = (response, queries, composeRevenueInfo) => {
  const { queryType } = composeRevenueInfo;
  const paymentQuery = queries[2];
  const dauQuery = queries[3];
  const [summaryPayment, uniqueUsersResult, payment, dau] = response;
  const { revenue, payers } = mapSummaryPaymentResult(queries[0], summaryPayment);
  const { uniqueUsers } = convertUniqueUsersResult(uniqueUsersResult);
  const { events, options, currencySymbol } = mapComposeRevenueResult(
    composeRevenueInfo,
    paymentQuery,
    dauQuery,
    payment,
    dau
  );
  return { revenue, payers, uniqueUsers, events, options, currencySymbol, queryType };
};

export const createCsvRevenueQuery = (state) => {
  const revenueQuery = createEventRevenueQuery(state);
  const { aggregations } = revenueQuery;
  const amountField = getRevenueAmountFieldPath(state);

  return {
    ...revenueQuery,
    // для сsv добавляются дополнительные агрегации помимо тех которые создаются для revenue запроса.
    aggregations: aggregations.concat(
      getRevenueAggregationsCsv(
        amountField,
        aggregations.map(({ type }) => type)
      )
    ),
  };
};

export const getRevenueCsvInfo = (state) => {
  const getType = (field) => getTypeField(field, state);

  return { getType };
};

export const fromRevenueCsvResponse = (response, query, revenueCsvInfo) => {
  const { getType } = revenueCsvInfo;
  const result = toEventResult(response, query, '', getType);
  const { info } = result;
  const columns = [
    info.groupBy,
    info.splitBy,
    COLUMNS_CSV.COUNT,
    COLUMNS_CSV.TOTAL,
    COLUMNS_CSV.MAX,
    COLUMNS_CSV.MIN,
  ].filter((i) => !!i);
  return toCsvEvent(result, columns, getType);
};
